import ApiClient from "../ApiClient";
import { LeadStatus } from "../constants/leadStatus";

const SOURCE_LABEL_MAP = Object.freeze({
  0: "Website",
  1: "Referral",
  2: "LinkedIn",
  3: "Facebook",
  4: "Instagram",
  5: "WhatsApp",
  6: "Email Campaign",
});

const dateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function parseLeadDate(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const numericDate = new Date(value);
    return Number.isNaN(numericDate.getTime()) ? null : numericDate;
  }

  if (typeof value !== "string") return null;

  const normalizedValue = value.trim();
  if (!normalizedValue) return null;

  const preformattedDateMatch = normalizedValue.match(
    /^(\d{2})\s([A-Za-z]{3})\s(\d{4})$/,
  );
  if (preformattedDateMatch) {
    const parsedDate = new Date(
      `${preformattedDateMatch[2]} ${preformattedDateMatch[1]}, ${preformattedDateMatch[3]} UTC`,
    );
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const isoDate = new Date(normalizedValue);
  if (!Number.isNaN(isoDate.getTime())) {
    return isoDate;
  }

  const yyyyMmDdMatch = normalizedValue.match(
    /^(\d{4})[-/](\d{2})[-/](\d{2})(?:[T\s].*)?$/,
  );
  if (yyyyMmDdMatch) {
    const [, year, month, day] = yyyyMmDdMatch;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  }

  const ddMmYyyyMatch = normalizedValue.match(
    /^(\d{2})[-/](\d{2})[-/](\d{4})(?:[T\s].*)?$/,
  );
  if (ddMmYyyyMatch) {
    const [, day, month, year] = ddMmYyyyMatch;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  }

  const verboseDateMatch = normalizedValue.match(
    /^([A-Za-z]+)\s+(\d{1,2})\s+(\d{4})(?:\s+at\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/i,
  );
  if (verboseDateMatch) {
    const [, monthName, day, year] = verboseDateMatch;
    const parsedDate = new Date(`${monthName} ${day}, ${year} UTC`);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
}

function formatLeadDate(value) {
  if (!value || value === "-") return "-";
  const date = parseLeadDate(value);
  if (!date) return "-";
  return dateFormatter.format(date);
}

function resolveLeadSource(source) {
  if (typeof source === "number") {
    return SOURCE_LABEL_MAP[source] ?? `Source ${source}`;
  }

  return source || "-";
}

function normalizeLeadValue(value) {
  if (value == null || value === "") return "-";
  return value;
}

function resolveLeadPhoneNumber(lead) {
  const countryCode =
    lead?.CountryCode ?? lead?.countryCode ?? lead?.country_code ?? "";
  const whatsAppNo =
    lead?.WhatsAppNo ??
    lead?.whatsAppNo ??
    lead?.whatsappNo ??
    lead?.phoneNumber ??
    lead?.PhoneNumber ??
    "";

  const combinedPhoneNumber = `${countryCode}${whatsAppNo}`.trim();
  if (combinedPhoneNumber) {
    return combinedPhoneNumber;
  }

  return normalizeLeadValue(lead?.phoneNumber ?? lead?.PhoneNumber);
}

function normalizeLead(lead, index = 0) {
  const firstName = lead?.FirstName?.trim() ?? lead?.firstName?.trim() ?? "";
  const lastName = lead?.LastName?.trim() ?? lead?.lastName?.trim() ?? "";
  const fullName = `${firstName} ${lastName}`.trim();

  return {
    rowKey:
      lead?.leadId ??
      lead?.LeadId ??
      lead?.id ??
      lead?.Id ??
      `lead-${index + 1}`,
    name: normalizeLeadValue(lead?.name ?? lead?.Name ?? fullName),
    email: normalizeLeadValue(lead?.email ?? lead?.Email),
    status:
      lead?.leadsStatus ??
      lead?.LeadsStatus ??
      lead?.status ??
      lead?.Status ??
      lead?.leadStatus ??
      LeadStatus.NEW_LEADS,
    source: resolveLeadSource(
      lead?.source ?? lead?.Source ?? lead?.leadSource ?? lead?.LeadSource,
    ),
    company: normalizeLeadValue(lead?.company ?? lead?.Company),
    phoneNumber: resolveLeadPhoneNumber(lead),
    notes: normalizeLeadValue(lead?.notes ?? lead?.Notes),
    businessDetails: normalizeLeadValue(
      lead?.businessDetails ?? lead?.BusinessDetails,
    ),
    followupDate: formatLeadDate(
      lead?.followupDate ?? lead?.FollowupDate ?? lead?.followUpDate,
    ),
    contactOn: formatLeadDate(lead?.contactOn ?? lead?.ContactOn),
  };
}

function normalizeCursorResponse(response) {
  const payload = response?.data?.data ?? response?.data ?? {};
  const leadItems =
    payload?.leadDetails ??
    payload?.items ??
    payload?.data ??
    payload?.records ??
    payload?.leads ??
    [];

  return {
    data: Array.isArray(leadItems)
      ? leadItems.map((lead, index) => normalizeLead(lead, index))
      : [],
    nextCursor:
      payload?.nextCursor ??
      payload?.cursor ??
      payload?.pagination?.nextCursor ??
      null,
  };
}

const LeadService = {
  async getLeads() {
    const resp = await ApiClient.get(`/lead/leads-dashboard`);
    return resp;
  },

  async getLeadsCursor({ limit = 20, cursor = null, search = "" } = {}) {
    const response = await ApiClient.get(`/lead/leads-cursor`, {
      params: {
        limit,
        ...(cursor ? { cursor } : {}),
        ...(search ? { search } : {}),
      },
    });

    return normalizeCursorResponse(response);
  },

  async addLead(lead) {
    console.log("LeadService.addLead called with:", lead);
    const resp = await ApiClient.post(`/lead/add-lead-request`, lead);
    return resp;
  },

  async updateLead(lead) {
    console.log("LeadService.updateLead called with:", lead);
    const resp = await ApiClient.put(`/lead/update-lead`, lead);
    console.log("update lead response:", resp);
    return resp;
  },

  async getLeadsInfo() {
    const resp = await ApiClient.get(`/lead/leads`);
    return resp;
  },

  async insertBulkLead(payload) {
    const resp = await ApiClient.post("/lead/bulk-lead-insert", payload);
    return resp;
  },

  async getAllLeadGroups(queryParams) {
    const resp = await ApiClient.get("/Lead/lead-group-list", {
      params: queryParams,
    });
    return resp;
  },

  async createLeadGroup(payload) {
    const resp = await ApiClient.post("/Lead/create-lead-group", payload);
    return resp;
  },

  async updateLeadGroup(payload) {
    const resp = await ApiClient.put("/Lead/update-lead-group", payload);
    return resp;
  },

  async deleteLeadGroup(id) {
    const resp = await ApiClient.delete(`/Lead/delete-lead-group/${id}`);
    return resp;
  },

  async getLeadAutoSuggestion(searchText) {
    const resp = await ApiClient.get("/Lead/lead-auto-suggestion", {
      params: {
        search: searchText,
      },
    });
    return resp;
  },
};

export default LeadService;
